import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";

import {
  FiPlus,
  FiEye,
  FiSearch,
  FiUser, // Reusing FiUser for Supplier
  FiDollarSign,
  FiCalendar,
  FiChevronsUp,
  FiChevronsDown,
  FiCreditCard,
  FiAlertTriangle,
  FiList,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiTruck, // Using FiTruck or similar for supplier/vendor context
} from "react-icons/fi";

// --- MOCK DATA / UTILS ---
// MOCK DATA is renamed and adjusted for Bills (Accounts Payable) context
const MOCK_BILLS = [
  { id: "BILL-001", supplierName: "Office Supplies Inc", billDate: "2025-12-01", dueDate: "2025-12-16", grandTotal: 500.0, status: "Paid" },
  { id: "BILL-002", supplierName: "Raw Material Co", billDate: "2025-11-25", dueDate: "2025-12-10", grandTotal: 3500.5, status: "Overdue" },
  { id: "BILL-003", supplierName: "Utilities Provider", billDate: "2025-12-05", dueDate: "2025-12-20", grandTotal: 120.75, status: "Pending" },
  { id: "BILL-004", supplierName: "Office Supplies Inc", billDate: "2025-12-08", dueDate: "2025-12-23", grandTotal: 150.0, status: "Draft" },
  { id: "BILL-005", supplierName: "Marketing Agency", billDate: "2025-10-15", dueDate: "2025-10-30", grandTotal: 8000.0, status: "Paid" },
  { id: "BILL-006", supplierName: "Raw Material Co", billDate: "2025-11-01", dueDate: "2025-11-15", grandTotal: 900.0, status: "Overdue" },
  { id: "BILL-007", supplierName: "Utilities Provider", billDate: "2025-12-09", dueDate: "2025-12-24", grandTotal: 200.0, status: "Pending" },
  { id: "BILL-008", supplierName: "IT Services Ltd", billDate: "2025-09-10", dueDate: "2025-09-25", grandTotal: 1500.0, status: "Paid" },
  { id: "BILL-009", supplierName: "Logistics Partner", billDate: "2025-08-20", dueDate: "2025-09-04", grandTotal: 750.0, status: "Overdue" },
  { id: "BILL-010", supplierName: "Hosting Service", billDate: "2025-12-10", dueDate: "2025-12-25", grandTotal: 1800.0, status: "Pending" },
  { id: "BILL-011", supplierName: "HR Consultant", billDate: "2025-07-01", dueDate: "2025-07-15", grandTotal: 2200.0, status: "Paid" },
  { id: "BILL-012", supplierName: "Equipment Lease", billDate: "2025-11-15", dueDate: "2025-11-30", grandTotal: 1000.0, status: "Overdue" },
];

const formatCurrency = (amount) => {
  return Number(amount).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Pagination component (internal) - Unchanged
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;

  // visible window up to 5 pages
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3) {
    start = 1;
    end = Math.min(5, totalPages);
  } else if (currentPage + 2 >= totalPages) {
    end = totalPages;
    start = Math.max(1, totalPages - 4);
  }

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <nav aria-label="pagination">
      <ul className="pagination mb-0">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
            First
          </button>
        </li>
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            <FiChevronLeft /> Prev
          </button>
        </li>

        {pages.map((p) => (
          <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
            <button className="page-link" onClick={() => onPageChange(p)}>
              {p}
            </button>
          </li>
        ))}

        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next <FiChevronRight />
          </button>
        </li>
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
            Last
          </button>
        </li>
      </ul>
    </nav>
  );
};

// --- MAIN COMPONENT ---
function SupplierBills() {
  // Renamed state from invoices to bills
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  // Changed default sort key from invoiceDate to billDate
  const [sortBy, setSortBy] = useState({ key: "billDate", direction: "desc" });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch mock data (now bills)
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      // Use MOCK_BILLS
      setBills(MOCK_BILLS);
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  // Memoized processed list (filter + sort)
  const processedBills = useMemo(() => {
    // Use bills
    let list = [...bills];

    // filter by search
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.id.toLowerCase().includes(s) ||
          i.supplierName.toLowerCase().includes(s) || // Filter by supplierName
          String(i.grandTotal).toLowerCase().includes(s)
      );
    }

    // status filter
    if (statusFilter !== "All") list = list.filter((i) => i.status === statusFilter);

    // date range filter (billDate)
    if (dateRange.start) list = list.filter((i) => i.billDate >= dateRange.start);
    if (dateRange.end) list = list.filter((i) => i.billDate <= dateRange.end);

    // sort
    list.sort((a, b) => {
      const aVal = a[sortBy.key];
      const bVal = b[sortBy.key];

      let cmp = 0;
      if (sortBy.key === "grandTotal") cmp = Number(aVal) - Number(bVal);
      // Compare by supplierName instead of customerName
      else if (sortBy.key === "supplierName") cmp = String(aVal).localeCompare(String(bVal));
      else cmp = String(aVal).localeCompare(String(bVal));

      return sortBy.direction === "asc" ? cmp : -cmp;
    });

    return list;
    // Depend on bills
  }, [bills, searchTerm, statusFilter, dateRange, sortBy]);

  // Reset page to 1 when filters change - Unchanged
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, sortBy, itemsPerPage]);

  // pagination calculations
  const totalItems = processedBills.length;
  // const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage)); // totalPages not strictly needed here
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated = processedBills.slice(startIndex, endIndex);

  // KPIs
  const kpis = useMemo(() => {
    const total = bills.length;
    // Total bills paid by us
    const totalPaid = bills.filter((i) => i.status === "Paid").length;
    // Total amount currently due/overdue
    const totalPendingAmount = bills.filter((i) => i.status === "Pending" || i.status === "Overdue").reduce((s, i) => s + i.grandTotal, 0);
    // Total bills overdue (count)
    const totalOverdue = bills.filter((i) => i.status === "Overdue").length;
    return {
      total,
      totalPaid,
      totalPendingAmount: formatCurrency(totalPendingAmount),
      totalOverdue, // Added overdue count for a clearer KPI
    };
    // Depend on bills
  }, [bills]);

  // Sorting handler - Unchanged
  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "desc" };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return null;
    return sortBy.direction === "asc" ? <FiChevronsUp size={14} className="ms-1 text-muted" /> : <FiChevronsDown size={14} className="ms-1 text-muted" />;
  };

  const getStatusBadge = (status) => {
    let cls = "bg-secondary";
    if (status === "Paid") cls = "bg-success";
    else if (status === "Overdue") cls = "bg-danger";
    else if (status === "Pending") cls = "bg-warning text-dark";
    else if (status === "Draft") cls = "bg-info text-dark";
    return <span className={`badge ${cls} status-badge`}>{status}</span>;
  };

  // actions (stubs)
  const handleViewBill = (id) => {
    // wire up to navigation or modal in your app
    alert(`View Supplier Bill ${id}`);
  };
  const handleCreateNew = () => {
    alert("Go to create supplier bill page");
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="page-inner">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-2 d-flex align-items-center">
                {/* Changed icon and title */}
                <FiTruck className="me-2 " />All Supplier Bills
              </h2>
              <small className="text-muted">Manage received bills, track payments & outstanding amounts (Accounts Payable)</small>
            </div>

            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <button className="btn btn-outline-secondary d-none d-sm-inline-flex align-items-center" onClick={() => { setSearchTerm(""); setStatusFilter("All"); setDateRange({ start: "", end: "" }); }}>
                Reset Filters
              </button>
              <button className="btn btn-primary btn-pill d-flex align-items-center" onClick={handleCreateNew}>
                <FiPlus className="me-2" /> Create Bill
              </button>
            </div>
          </div>

           

          {/* Main Card */}
          <div className="card card-ghost p-0">
            {/* Filter bar */}
            <div className="p-3" style={{ gap: 12 }}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center ">
                  <small className="text-muted me-2">Show</small>
                  <select className="form-select form-select-sm" style={{ width: 80 }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={MOCK_BILLS.length}>All</option>
                  </select>
                </div>

                <div className="input-group search-input" style={{ width: 300 }}>
                  {/* Updated search placeholder */}
                  <input type="text" className="form-control form-control-sm" placeholder="Search ID, supplier or amount..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  <span className="input-group-text"><FiSearch /></span>
                </div>
              </div>


            </div>

            {/* Table */}
            <div className=" p-2">
              {loading ? (
                <div className="p-5 text-center">
                  <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                  <div className="mt-2 text-muted">Loading bills...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 ">
                    <thead className=" ">
                      <tr>
                        {/* Updated column headers and sort keys */}
                        <th style={{ width: "12%" }} onClick={() => handleSort("id")} className="cursor-pointer">Bill ID <SortIcon columnKey="id" /></th>
                        <th style={{ width: "28%" }} onClick={() => handleSort("supplierName")} className="cursor-pointer">Supplier <SortIcon columnKey="supplierName" /></th>
                        <th style={{ width: "14%" }} onClick={() => handleSort("billDate")} className="cursor-pointer">Bill Date <SortIcon columnKey="billDate" /></th>
                        <th style={{ width: "14%" }} onClick={() => handleSort("dueDate")} className="cursor-pointer">Payment Due <SortIcon columnKey="dueDate" /></th>
                        <th style={{ width: "12%", textAlign: "right" }} onClick={() => handleSort("grandTotal")} className="cursor-pointer">Amount <SortIcon columnKey="grandTotal" /></th>
                        <th style={{ width: "12%" }}>Status</th>
                        <th style={{ width: "8%" }}>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center p-5 text-muted">
                            <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                            No bills found. Try adjusting filters or creating a new bill.
                          </td>
                        </tr>
                      ) : (
                        paginated.map((bill) => ( // Changed inv to bill
                          <tr key={bill.id}>
                            <td><strong>{bill.id}</strong></td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10 }}>
                                  <FiUser className="text-muted" />
                                </div>
                                <div>
                                  {/* Changed customerName to supplierName and Invoice to Bill */}
                                  <div className="fw-bold">{bill.supplierName}</div>
                                  <small className="text-muted">Bill: {bill.id}</small>
                                </div>
                              </div>
                            </td>
                            <td>{bill.billDate}</td>
                            <td className={bill.status === "Overdue" ? "text-danger fw-bold" : ""}>{bill.dueDate}</td>
                            {/* Amount we owe is typically displayed in a primary color (like primary/info) or just regular text, but using text-danger for outstanding bills is also common. Keeping original text-success for consistency/simplification */}
                            <td className="text-end text-success fw-bold">{formatCurrency(bill.grandTotal)}</td>
                            <td>{getStatusBadge(bill.status)}</td>
                            <td>
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <button className="btn btn-sm btn-outline-info" title="View" onClick={() => handleViewBill(bill.id)}><FiEye /></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer / Pagination */}
            <div className="card-footer d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {totalItems === 0 ? 0 : startIndex + 1} to {endIndex} of {totalItems} entries
              </div>
              <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, Math.max(1, Math.ceil(totalItems / itemsPerPage)))))} />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default SupplierBills;